import type { Alert, AlertDefinitionContent, AlertEnhanced } from '@/types/alerts/alert.model'
import type { ColorObject } from '@/types/app/color.type'
import { ref } from 'vue'
import IconBattery from '@/assets/icons/battery.svg'
import IconBatteryBank from '@/assets/icons/battery_bank.svg'
import IconEnergyConsumer from '@/assets/icons/energy_consumer.svg'
import IconNotification from '@/assets/icons/notifications.svg'
import IconOrganisation from '@/assets/icons/organisation.svg'
import IconAlert from '@/assets/icons/release_alert.svg'
import IconSite from '@/assets/icons/site.svg'
import { getAssetFamilyTypeName } from '@/utils/asset-family-type'
import { SEVERITY_COLORS } from '@/utils/constants/colors'
import { ALERT_DEFINITIONS } from '@/utils/constants/definitions'
import { ALERT_CLASS_LABELS, ALERT_LEVEL_LABELS, ALERT_SEVERITY_LABELS, ALERT_STATUS_LABELS } from '@/utils/constants/labels'
import { SEVERITY_PATTERNS } from '@/utils/constants/patterns'

export function useAlerts() {
  function getAlertClassIcon(alertClass: number) {
    return alertClass ? IconNotification : IconAlert
  }

  function getAlertClassLabel(alertClass: number): string {
    return ALERT_CLASS_LABELS[alertClass] ?? ''
  }

  function getAlertDefinitionContent(alertDefinitionId: string): AlertDefinitionContent {
    return (
      ALERT_DEFINITIONS[alertDefinitionId as keyof typeof ALERT_DEFINITIONS] ?? {
        description: `--`,
        title: '--',
      } as AlertDefinitionContent
    )
  }

  function getAlertDefinitionTitle(alertDefinitionId: string): string | undefined {
    const _alertDefinition = ALERT_DEFINITIONS[alertDefinitionId as keyof typeof ALERT_DEFINITIONS]
    return (
      _alertDefinition?.title ?? undefined
    )
  }

  function getAlertLevelIcon(level: number): string {
    switch (level) {
      case 0:
        return IconOrganisation
      case 1:
        return IconSite
      case 2:
        return IconBatteryBank
      case 3:
        return IconBattery
      case 4:
        return IconEnergyConsumer
      default:
        return IconBattery
    }
  }

  function getAlertLevelLabel(level: number, sourceTypeId: string): string {
    if (level === 2 && sourceTypeId) {
      return getAssetFamilyTypeName(sourceTypeId)
    }

    return ALERT_LEVEL_LABELS[level] ?? ''
  }

  function getAlertSeverityColor(severity: number): ColorObject {
    return SEVERITY_COLORS[severity as keyof typeof SEVERITY_COLORS] as ColorObject
  }

  function getAlertSeverityLabel(severity: number): string {
    return ALERT_SEVERITY_LABELS[severity] ?? ''
  }

  function getAlertSeverityPattern(level: number): string {
    return SEVERITY_PATTERNS[level] ?? ''
  }

  function getAlertStatusLabel(status: number): string {
    return ALERT_STATUS_LABELS[status] ?? ''
  }

  function getAlertEnhanced(alerts: Alert[] | undefined): AlertEnhanced[] | [] {
    if (!alerts || !alerts.length) {
      return []
    }

    return alerts.map((alert) => {
      return {
        ...alert,
        definition: getAlertDefinitionContent(alert.alertDefinitionId),
        severityLabel: getAlertSeverityLabel(alert.severity),
        statusLabel: getAlertStatusLabel(alert.status),
      }
    })
  }

  const enhancedData = ref<AlertEnhanced[]>([])

  function enhanceData(data: Alert[]) {
    enhancedData.value = getAlertEnhanced(data)
  }

  const search = ref<string>('')

  function isFiltered(alert: AlertEnhanced) {
    return (
      alert.definition.description.toLocaleLowerCase().includes(search.value.toLocaleLowerCase())
      || alert.definition.title.toLocaleLowerCase().includes(search.value.toLocaleLowerCase())
      || alert.sourceName.toLocaleLowerCase().includes(search.value.toLocaleLowerCase())
    )
  }

  return {
    enhanceData,
    enhancedData,
    getAlertClassIcon,
    getAlertClassLabel,
    getAlertDefinitionContent,
    getAlertDefinitionTitle,
    getAlertEnhanced,
    getAlertLevelIcon,
    getAlertLevelLabel,
    getAlertSeverityColor,
    getAlertSeverityPattern,
    getAlertSeverityLabel,
    getAlertStatusLabel,
    isFiltered,
    search,
  }
}
